import { useParams, Link, useLocation } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaJsonLd from '@/components/base/SchemaJsonLd';
import { allMovies, latestUpdates } from '@/mocks/movies';

const categoryMap: Record<string, string> = {
  movie: '电影',
  tv: '电视剧',
  anime: '动漫',
  variety: '综艺',
};

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const movieId = parseInt(id || '1', 10);
  const movie = allMovies.find((m) => m.id === movieId);

  const relatedMovies = useMemo(
    () => allMovies.filter((m) => m.id !== movieId && m.category === movie?.category).slice(0, 6),
    [movieId, movie?.category]
  );
  const otherUpdates = useMemo(
    () => latestUpdates.filter((m) => m.id !== movieId).slice(0, 4),
    [movieId]
  );

  const categoryName = movie ? categoryMap[movie.category] || movie.category : '';

  useEffect(() => {
    if (movie) {
      document.title = `${movie.title} - 屁屁影院 | ${categoryName}在线观看`;
      const desc = document.querySelector('meta[name="description"]');
      if (desc) desc.setAttribute('content', `${movie.title}在线观看 - ${movie.description}`);
    } else {
      document.title = '内容未找到 - 屁屁影院';
    }
  }, [movie, categoryName]);

  const schemas = useMemo(() => {
    if (!movie) return [];
    const fullUrl = `https://pipicinema.com${location.pathname}`;
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: movie.title,
        description: movie.description,
        image: movie.image,
        datePublished: `${movie.year}-01-01`,
        dateModified: `${movie.year}-01-01`,
        author: {
          '@type': 'Organization',
          name: '屁屁影院',
          url: 'https://pipicinema.com/',
        },
        publisher: {
          '@type': 'Organization',
          name: '屁屁影院',
          logo: {
            '@type': 'ImageObject',
            url: 'https://pipicinema.com/logo.png',
          },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': fullUrl,
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首页',
            item: 'https://pipicinema.com/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: categoryName,
            item: `https://pipicinema.com/category/${movie.category}`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: movie.title,
            item: fullUrl,
          },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Movie',
        name: movie.title,
        description: movie.description,
        image: movie.image,
        dateCreated: `${movie.year}-01-01`,
        genre: movie.tags,
        director: movie.director ? { '@type': 'Person', name: movie.director } : undefined,
        actor: movie.actors?.map((actor) => ({ '@type': 'Person', name: actor })),
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: movie.rating,
          bestRating: '10',
          worstRating: '1',
        },
      },
    ];
  }, [movie, categoryName, location.pathname]);

  if (!movie) {
    return (
      <>
        <Navbar />
        <Breadcrumb />
        <main className="min-h-[60vh] flex items-center justify-center px-4">
          <div className="text-center">
            <i className="ri-movie-2-line text-5xl text-foreground-300 mb-4"></i>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">内容未找到</h1>
            <p className="text-foreground-500 mb-4">抱歉，该影视内容不存在或已被下架</p>
            <Link
              to="/"
              className="inline-block px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SchemaJsonLd schemas={schemas} />
      <Navbar />
      <Breadcrumb />

      <main className="px-4 md:px-6 max-w-[1400px] mx-auto pb-16">
        <article className="mb-12" itemScope itemType="https://schema.org/Movie">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="w-full lg:w-72 shrink-0">
              <div className="rounded-lg overflow-hidden aspect-[2/3]">
                <img
                  src={movie.image}
                  alt={`屁屁影院 - ${movie.title}`}
                  title={`屁屁影院${movie.title}在线观看`}
                  className="w-full h-full object-cover object-top"
                  itemProp="image"
                />
              </div>
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-semibold">
                  {movie.rating}
                </span>
                <span className="px-2.5 py-1 rounded-md bg-secondary-500 text-background-50 text-xs font-semibold">
                  {categoryName}
                </span>
                <span className="text-sm text-foreground-500" itemProp="dateCreated">
                  {movie.year}
                </span>
              </div>

              <h1
                className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4"
                style={{ fontFamily: "var(--font-heading)" }}
                itemProp="name"
              >
                {movie.title}
              </h1>

              <p className="text-sm text-foreground-600 leading-relaxed mb-6" itemProp="description">
                {movie.description}
              </p>

              <div className="space-y-3 text-sm">
                {movie.duration && (
                  <div className="flex items-start gap-2">
                    <i className="ri-time-line text-foreground-400 mt-0.5"></i>
                    <span className="text-foreground-500 shrink-0 w-14">时长</span>
                    <span className="text-foreground-800">{movie.duration}</span>
                  </div>
                )}
                {movie.director && (
                  <div className="flex items-start gap-2">
                    <i className="ri-user-star-line text-foreground-400 mt-0.5"></i>
                    <span className="text-foreground-500 shrink-0 w-14">导演</span>
                    <span className="text-foreground-800" itemProp="director">{movie.director}</span>
                  </div>
                )}
                {movie.actors && movie.actors.length > 0 && (
                  <div className="flex items-start gap-2">
                    <i className="ri-user-line text-foreground-400 mt-0.5"></i>
                    <span className="text-foreground-500 shrink-0 w-14">主演</span>
                    <span className="text-foreground-800" itemProp="actor">
                      {movie.actors.join('、')}
                    </span>
                  </div>
                )}
                {'episode' in movie && (movie as { episode?: string }).episode && (
                  <div className="flex items-start gap-2">
                    <i className="ri-list-check text-foreground-400 mt-0.5"></i>
                    <span className="text-foreground-500 shrink-0 w-14">进度</span>
                    <span className="text-foreground-800">{(movie as { episode?: string }).episode}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 mt-6">
                {movie.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-background-200/70 text-foreground-700"
                    itemProp="genre"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-8 flex items-center gap-3">
                <button className="px-8 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold text-sm transition-colors cursor-pointer flex items-center gap-2">
                  <i className="ri-play-circle-line text-lg"></i>
                  立即播放
                </button>
                <button className="px-6 py-3 rounded-full bg-background-100 hover:bg-background-200/70 text-foreground-800 font-medium text-sm transition-colors cursor-pointer flex items-center gap-2">
                  <i className="ri-heart-line text-lg"></i>
                  收藏
                </button>
                <button className="px-6 py-3 rounded-full bg-background-100 hover:bg-background-200/70 text-foreground-800 font-medium text-sm transition-colors cursor-pointer flex items-center gap-2">
                  <i className="ri-share-line text-lg"></i>
                  分享
                </button>
              </div>
            </div>
          </div>

          <div className="mt-10 rounded-xl bg-background-100 p-6 md:p-8">
            <h2 className="text-lg font-bold text-foreground-900 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
              播放线路
            </h2>
            <div className="flex flex-wrap gap-2">
              {['线路一', '线路二', '线路三', '线路四'].map((line, i) => (
                <button
                  key={line}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                    i === 0
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-200/70 text-foreground-700 hover:bg-background-300/60'
                  }`}
                >
                  {line}
                </button>
              ))}
            </div>
            <div className="mt-6 rounded-lg bg-background-950 aspect-video flex items-center justify-center">
              <div className="text-center">
                <i className="ri-play-circle-line text-5xl text-foreground-500 mb-3"></i>
                <p className="text-sm text-foreground-500">点击播放按钮开始观看</p>
              </div>
            </div>
          </div>
        </article>

        {relatedMovies.length > 0 && (
          <section className="mb-10" aria-label="同类推荐">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-5" style={{ fontFamily: "var(--font-heading)" }}>
              同类推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              {relatedMovies.map((m) => (
                <Link
                  key={m.id}
                  to={`/article/${m.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative rounded-lg overflow-hidden aspect-[2/3] mb-2">
                    <img
                      src={m.image}
                      alt={`屁屁影院 - ${m.title}`}
                      title={`屁屁影院${m.title}在线观看`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-2 right-2">
                      <span className="px-2 py-0.5 rounded-md bg-accent-500 text-background-50 text-xs font-semibold">
                        {m.rating}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
                    {m.title}
                  </h3>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section aria-label="最新更新">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-5" style={{ fontFamily: "var(--font-heading)" }}>
            最新更新
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {otherUpdates.map((m) => (
              <Link
                key={m.id}
                to={`/article/${m.id}`}
                className="group cursor-pointer"
              >
                <div className="relative rounded-lg overflow-hidden aspect-[2/3] mb-2">
                  <img
                    src={m.image}
                    alt={`屁屁影院 - ${m.title}`}
                    title={`屁屁影院${m.title}在线观看`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-2 right-2">
                    <span className="px-2 py-0.5 rounded-md bg-secondary-500 text-background-50 text-xs font-semibold">
                      {m.episode}
                    </span>
                  </div>
                </div>
                <h3 className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
                  {m.title}
                </h3>
              </Link>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}